/**
 * Search Presentation Tool
 * 
 * Internal tool for the In-Presentation Search Agent to search within
 * PowerPoint presentations using various search types.
 * 
 * Uses the unified search endpoint on the Python backend.
 */

import { ToolDescription, safeParseToolParams } from "../../../../base/toolDescription";
import { ToolBase } from "../../../../base/toolBase";
import { backendClient } from "../../../../../../services/communication/backendClient";
import { createCompositeId } from "../../../../../core/compositeIdUtils";
import { getContextResolver } from "../../../../../core/contextResolver";
import { 
    SearchPresentationParamsSchema, 
    type SearchPresentationParams,
    type SearchType,
    type ResultType,
} from "./searchPresentation.schema";

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════════

// ───────────────────────────────────────────────────────────────────────────
// Enrichment Data Types
// ───────────────────────────────────────────────────────────────────────────

/** Shape data for enriched results */
export interface ShapeData {
    /** Unique shape identifier */
    shapeId: string;
    /** Shape type (text_box, rectangle, picture, chart, table, etc.) */
    shapeType?: string;
    /** Raw text content of the shape */
    text?: string;
    /** Bounding box in points */
    bbox?: { x: number; y: number; width: number; height: number };
    /** Text bounding box in points (may differ from shape bbox) */
    textBbox?: { x: number; y: number; width: number; height: number };
    /** Shape origin: 'master', 'layout', or 'slide' */
    origin?: string;
    /** Style signature hash for matching similar styles */
    styleSignature?: string;
    /** Whether the shape has meaningful content */
    hasContent?: boolean;
    /** Whether the shape is a placeholder */
    isPlaceholder?: boolean;
}

/** LLM analysis data for enriched results */
export interface AnalysisData {
    /** LLM-generated description of the slide/content */
    description?: string;
    /** Per-shape image descriptions keyed by shape_id */
    imageDescriptions?: Record<string, string>;
    /** Detected component regions with descriptions and bounds */
    componentBoxes?: Array<Record<string, unknown>>;
}

/** Layout/master metadata for enriched results */
export interface LayoutData {
    /** Layout template ID */
    layoutId?: string;
    /** Human-readable layout name (e.g., 'Title Slide', 'Two Content') */
    layoutName?: string;
    /** Master slide ID */
    masterId?: string;
    /** Master slide name (e.g., 'Corporate Theme') */
    masterName?: string;
    /** Layout description from analysis */
    description?: string;
}

// ───────────────────────────────────────────────────────────────────────────
// Search Result Types
// ───────────────────────────────────────────────────────────────────────────

/** Single search result from unified search */
export interface SearchResult {
    /** Type of result: slide, component, asset, shape, layout */
    resultType: ResultType;
    /** Unique result ID (slide_257, component_abc123, etc.) */
    resultId: string;
    /** Context ID for referencing this result (e.g., "d0|slide_257", "component_abc123") */
    contextId?: string;
    /** Presentation/deck ID */
    presentationId: string;
    /** Slide ID if result is on a slide */
    slideId?: string;
    /** 0-based slide index */
    slideIndex?: number;
    /** Shape ID for shape results */
    shapeId?: string;
    /** Shape type for shape results */
    shapeType?: string;
    /** Layout ID for layout results */
    layoutId?: string;
    /** Layout name for layout results */
    layoutName?: string;
    /** Relevance score (higher is better) */
    score: number;
    /** Cosine similarity for semantic/visual search (0-1) */
    similarity?: number;
    /** Description of the result */
    description?: string;
    /** Raw text content */
    text?: string;
    /** Base64-encoded image (optional, for visual context) */
    imageBase64?: string;
    /** Additional metadata */
    metadata?: Record<string, unknown>;
    /** Component SlideML payload (component results only, when available) */
    slideml?: Record<string, unknown>;
    
    // ───────────────────────────────────────────────────────────────────────
    // Enriched Data (populated when enrich_results=true)
    // ───────────────────────────────────────────────────────────────────────
    
    /** Individual shapes on the slide/in the component */
    shapes?: ShapeData[];
    /** LLM analysis data including descriptions and detected components */
    analysis?: AnalysisData;
    /** Layout/master template metadata */
    layout?: LayoutData;
}

/** Processed response from search */
export interface SearchPresentationResponse {
    /** Search results */
    results: SearchResult[];
    /** Number of results */
    count: number;
    /** Search type used */
    searchType: SearchType;
    /** Result types requested */
    resultTypes: ResultType[];
    /** The query if text search */
    query?: string;
    /** Deck scope searched (single deck, deck set, or ALL) */
    documentId: string;
    /** Search time in milliseconds */
    searchTimeMs?: number;
    /** 
     * Image IDs for automatic injection in next user message.
     * Convention used by extractImageIds() in messageBuilder.
     * Images are stored in contextResolver and referenced by these IDs.
     */
    _imageIds?: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// DECK RESOLUTION HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Resolve documentId parameter to actual deck ID.
 * Handles: numeric index (0, 1, 2), "CURRENT", or explicit deck ID.
 * 
 * Note: This is a simplified implementation. The full version would
 * use the DeckRegistry to resolve indexes to deck IDs.
 */
function unwrapQuotedSelector(value: string): string {
    const trimmed = value.trim();
    if (trimmed.length >= 2) {
        const quoteWrapped =
            (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
            (trimmed.startsWith("'") && trimmed.endsWith("'"));
        if (quoteWrapped) {
            return trimmed.slice(1, -1).trim();
        }
    }
    if (trimmed.length >= 4) {
        const escapedQuoteWrapped =
            (trimmed.startsWith('\\"') && trimmed.endsWith('\\"')) ||
            (trimmed.startsWith("\\'") && trimmed.endsWith("\\'"));
        if (escapedQuoteWrapped) {
            return trimmed.slice(2, -2).trim();
        }
    }
    return trimmed;
}

function normalizeDocumentSelector(value: string | number): string | number {
    if (typeof value === "number") {
        return value;
    }

    let normalized = value.trim();
    for (let i = 0; i < 3; i++) {
        const unwrapped = unwrapQuotedSelector(normalized);
        if (unwrapped === normalized) break;
        normalized = unwrapped;
    }

    if (/^\d+$/.test(normalized)) {
        return Number.parseInt(normalized, 10);
    }

    if (normalized.toUpperCase() === "CURRENT") return "CURRENT";
    if (normalized.toUpperCase() === "ALL") return "ALL";
    return normalized;
}

function resolvedocumentId(documentIdParam: string | number, deckRegistry?: Map<number, string>): string {
    const normalizedSelector = normalizeDocumentSelector(documentIdParam);
    if (normalizedSelector !== documentIdParam) {
        console.warn(
            `[SearchPresentationTool] Normalized documentId selector from "${String(documentIdParam)}" to "${String(normalizedSelector)}"`
        );
    }

    // Handle numeric index
    if (typeof normalizedSelector === 'number') {
        if (deckRegistry && deckRegistry.has(normalizedSelector)) {
            return deckRegistry.get(normalizedSelector)!;
        }
        // Fallback: return as deck_{index} - will be resolved on backend
        console.warn(`[SearchPresentationTool] Deck index ${normalizedSelector} not in registry, passing to backend`);
        return `deck_index_${normalizedSelector}`;
    }
    
    // Handle string values
    const documentIdStr = String(normalizedSelector);
    const normalizeddocumentId = documentIdStr.trim().toUpperCase();

    // "ALL" means search across the entire backend indexed library
    if (normalizeddocumentId === 'ALL') {
        return 'ALL';
    }
    
    // "CURRENT" means the currently active deck - resolved by caller
    if (normalizeddocumentId === 'CURRENT') {
        // This should be resolved by the agent before calling the tool
        console.warn('[SearchPresentationTool] "CURRENT" deck ID should be resolved by agent');
        return 'CURRENT';
    }
    
    // Already an explicit deck ID
    return documentIdStr;
}

// ═══════════════════════════════════════════════════════════════════════════
// SEARCH PRESENTATION TOOL
// ═══════════════════════════════════════════════════════════════════════════

// System prompt fallback for Keywords AI
const SEARCH_PRESENTATION_SYSTEM_PROMPT = `### searchPresentation
Search within PowerPoint presentations for slides, components, and content.

**When to use:**
- Finding slides with specific content
- Locating reusable components/templates
- Searching for similar layouts or styles

**Search types:**
- \`semantic\` (default) - Find conceptually related content
- \`exact\` - Find exact text matches
- \`fuzzy\` - Find approximate text matches
- \`visual\` - Find visually similar content

**Result types:**
- \`slide\` - Full slides
- \`component\` - Reusable shape groups
- \`shape\` - Individual shapes

**Required field:**
- \`uiDescription\` - Single-line status text for the tool card UI

**documentId options:**
- \`"CURRENT"\` - Current presentation only
- \`"d0"\`, \`"d1"\` - Deck by index
- \`"ALL"\` - Search all indexed presentations
- \`documentIds\` - Search a specific list of presentations

Prefer targeted deck(s) when you know the source. Use \`"ALL"\` for historical ideation and cross-library discovery.

\`\`\`json
{
  "uiDescription": "Searching for quarterly revenue slides",
  "documentId": "CURRENT",
  "searchType": "semantic",
  "resultTypes": ["slide", "component"],
  "query": "quarterly revenue chart",
  "numResults": 10
}
\`\`\``;

/**
 * Tool for searching within PowerPoint presentations.
 * 
 * Supports:
 * - Semantic search (Voyage embeddings + FAISS)
 * - Exact text search (SQLite FTS5)
 * - Fuzzy text search (approximate matching)
 * - Visual search (CLIP embeddings + FAISS)
 * 
 * Returns results with short IDs for efficient context passing.
 */
export class SearchPresentationTool extends ToolBase<SearchPresentationParams, SearchPresentationResponse> {
    public readonly name = "searchPresentation";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = SEARCH_PRESENTATION_SYSTEM_PROMPT;
    
    // Streaming operation fields for progressive UI updates
    fieldsToCheck = [
        { name: 'uiDescription', type: 'string' as const, list_status: false, order: 0 },
        { name: 'documentId', type: 'string' as const, list_status: false, order: 2 },
        { name: 'searchType', type: 'string' as const, list_status: false, order: 3 },
        { name: 'query', type: 'string' as const, list_status: false, order: 5 },
        { name: 'numResults', type: 'number' as const, list_status: false, order: 6 },
    ];
    
    listsToCheck = [
        {
            name: 'documentIds',
            type: 'array' as const,
            item_type: 'string' as const,
            list_status: true,
            order: 1,
        },
        { 
            name: 'resultTypes', 
            type: 'array' as const, 
            item_type: 'string' as const,
            list_status: true, 
            order: 4,
        },
    ];
    
    // Deck registry for resolving indexes to deck IDs
    private deckRegistry: Map<number, string> = new Map();
    private currentdocumentId?: string;
    
    constructor() {
        super();
        this.description = new ToolDescription(
            "searchPresentation",
            `Search within a PowerPoint presentation for slides, components, assets, shapes, or layouts.

## Search Types:
- **semantic**: Find content by meaning (e.g., "revenue chart" finds charts about revenue even without exact words)
- **exact**: Find exact text matches
- **fuzzy**: Find approximate text matches (handles typos)
- **visual**: Find visually similar content (requires visualSource)

## Result Types:
- **slide**: Full slide results
- **component**: Cropped component regions (grouped shapes)
- **asset**: Individual assets (tables, charts, images)
- **shape**: Individual shapes with text content
- **layout**: Master/layout templates

## Search Scope:
- **documentId**: Search one deck ("CURRENT", deck index, explicit deck ID, or "ALL")
- **documentIds**: Search across a specific list of decks
- Prefer specific deck(s) when known; use "ALL" for global ideation across backend indexed presentations
- **uiDescription**: Required one-line status for the tool call card

## Example - Semantic Search:
{
  "uiDescription": "Searching for revenue chart slides",
  "documentId": "CURRENT",
  "searchType": "semantic",
  "resultTypes": ["slide", "component"],
  "query": "quarterly revenue chart",
  "numResults": 5
}

## Example - Visual Search:
{
  "uiDescription": "Searching for visually similar slides",
  "documentId": 0,
  "searchType": "visual",
  "resultTypes": ["slide", "component"],
  "visualSource": {
    "slideIndex": 0
  },
  "numResults": 10
}

## Example - Multi Deck Search:
{
  "uiDescription": "Searching roadmap slides across selected decks",
  "documentIds": ["d0", "d2"],
  "searchType": "semantic",
  "resultTypes": ["slide"],
  "query": "roadmap timeline",
  "numResults": 8
}

Results include IDs for referencing (e.g., slide_257, component_abc123).`,
            SearchPresentationParamsSchema
        );
    }
    
    /**
     * Set the deck registry for resolving indexes to deck IDs.
     * Called by the agent before executing search.
     */
    public setDeckRegistry(registry: Map<number, string>, currentdocumentId?: string): void {
        this.deckRegistry = registry;
        this.currentdocumentId = currentdocumentId;
    }
    
    public async execute(parameters: unknown): Promise<SearchPresentationResponse> {
        const parseResult = safeParseToolParams(SearchPresentationParamsSchema, parameters);
        if (parseResult.success === false) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }
        
        const params = parseResult.data;

        const resolveDeckSelector = (selector: string | number): string => {
            let resolved = resolvedocumentId(selector, this.deckRegistry);
            if (resolved === 'CURRENT' && this.currentdocumentId) {
                resolved = this.currentdocumentId;
            }
            return resolved;
        };

        const defaultDeckSelector: string | number = params.documentId ?? 'CURRENT';
        let useAllScope = false;
        let resolveddocumentId: string | undefined;
        let resolveddocumentIds: string[] | undefined;

        if (Array.isArray(params.documentIds) && params.documentIds.length > 0) {
            const uniquedocumentIds = new Set<string>();
            for (const selector of params.documentIds) {
                const resolved = resolveDeckSelector(selector);
                if (resolved.toUpperCase() === 'ALL') {
                    useAllScope = true;
                    break;
                }
                uniquedocumentIds.add(resolved);
            }

            if (!useAllScope) {
                resolveddocumentIds = [...uniquedocumentIds];
            }
        } else {
            resolveddocumentId = resolveDeckSelector(defaultDeckSelector);
            if (resolveddocumentId.toUpperCase() === 'ALL') {
                useAllScope = true;
                resolveddocumentId = 'ALL';
            }
        }

        // Normalize a single-item documentIds list to single-deck behavior.
        if (!useAllScope && resolveddocumentIds && resolveddocumentIds.length === 1) {
            resolveddocumentId = resolveddocumentIds[0];
            resolveddocumentIds = undefined;
        }

        if (!useAllScope && !resolveddocumentId && (!resolveddocumentIds || resolveddocumentIds.length === 0)) {
            throw new Error('Unable to resolve search scope. Provide documentId, documentIds, or use documentId="ALL".');
        }

        const presentationIdsForBackend = !useAllScope && resolveddocumentIds && resolveddocumentIds.length > 0
            ? resolveddocumentIds
            : undefined;
        const documentIdForBackend = useAllScope ? 'ALL' : resolveddocumentId;
        const scopeLabel = useAllScope
            ? 'ALL indexed presentations'
            : presentationIdsForBackend
                ? `deck set [${presentationIdsForBackend.join(', ')}]`
                : `deck "${documentIdForBackend}"`;

        console.log(`[SearchPresentationTool] Searching ${scopeLabel} with ${params.searchType} search`);
        console.log(`[SearchPresentationTool] Result types: ${params.resultTypes.join(', ')}`);
        if (params.query) {
            console.log(`[SearchPresentationTool] Query: "${params.query.slice(0, 60)}..."`);
        }
        
        // Exclusion arrays are injected by the agent layer when excludeAllPrevious=true.
        let slidesToExclude = params.slidesToExclude?.map(String) || [];
        let componentsToExclude = params.componentsToExclude || [];
        let assetsToExclude = params.assetsToExclude || [];
        let shapesToExclude = params.shapesToExclude || [];
        let layoutsToExclude = params.layoutsToExclude || [];
        
        try {
            // Call the unified search endpoint via BackendClient
            const backendResponse = await backendClient.searchUnified({
                documentId: documentIdForBackend,
                presentationIds: presentationIdsForBackend,
                searchType: params.searchType,
                resultTypes: params.resultTypes,
                queries: params.query ? [params.query] : undefined,
                numResults: params.numResults ?? 10,
                enrichResults: true,
                slidesToInclude: params.slidesToInclude?.map(String),
                slidesToExclude: slidesToExclude.length > 0 ? slidesToExclude : undefined,
                componentIdsToExclude: componentsToExclude.length > 0 ? componentsToExclude : undefined,
                assetIdsToExclude: assetsToExclude.length > 0 ? assetsToExclude : undefined,
                layoutIdsToExclude: layoutsToExclude.length > 0 ? layoutsToExclude : undefined,
                shapeIdsToExclude: shapesToExclude.length > 0 ? shapesToExclude : undefined,
                visualSource: params.visualSource ? {
                    slideId: params.visualSource.slideId,
                    slideIndex: params.visualSource.slideIndex,
                    componentId: params.visualSource.componentId,
                    shapeIds: params.visualSource.shapeIds,
                } : undefined,
            });
            
            // Transform backend response to our response format and add contextId
            const results: SearchResult[] = (backendResponse.results || []).map((r: Record<string, unknown>) => {
                const resultType = r.result_type as ResultType;
                const resultId = r.result_id as string;
                const presentationId = r.presentation_id as string;
                const slideId = r.slide_id as string | undefined;
                const shapeId = r.shape_id as string | undefined;
                
                // Generate contextId based on result type (same logic as agent's generateContextId)
                let contextId: string;
                if (resultType === 'component') {
                    // Components are globally unique
                    contextId = resultId;
                } else if (resultType === 'layout') {
                    // Layouts are deck-scoped
                    contextId = createCompositeId(presentationId, resultId);
                } else if (resultType === 'shape' && slideId) {
                    // Shapes need full context
                    contextId = createCompositeId(presentationId, slideId, shapeId || resultId);
                } else if (resultType === 'slide') {
                    // Slides are deck-scoped
                    contextId = createCompositeId(presentationId, resultId);
                } else {
                    // Assets and others
                    contextId = createCompositeId(presentationId, resultId);
                }
                
                // Map enrichment data if present
                const shapes = r.shapes ? (r.shapes as Array<Record<string, unknown>>).map(s => ({
                    shapeId: s.shape_id as string,
                    shapeType: s.shape_type as string | undefined,
                    text: s.text as string | undefined,
                    bbox: s.bbox as { x: number; y: number; width: number; height: number } | undefined,
                    textBbox: s.text_bbox as { x: number; y: number; width: number; height: number } | undefined,
                    origin: s.origin as string | undefined,
                    styleSignature: s.style_signature as string | undefined,
                    hasContent: s.has_content as boolean | undefined,
                    isPlaceholder: s.is_placeholder as boolean | undefined,
                })) : undefined;
                
                const analysis = r.analysis ? {
                    description: (r.analysis as Record<string, unknown>).description as string | undefined,
                    imageDescriptions: (r.analysis as Record<string, unknown>).image_descriptions as Record<string, string> | undefined,
                    componentBoxes: (r.analysis as Record<string, unknown>).component_boxes as Array<Record<string, unknown>> | undefined,
                } : undefined;
                
                const layout = r.layout ? {
                    layoutId: (r.layout as Record<string, unknown>).layout_id as string | undefined,
                    layoutName: (r.layout as Record<string, unknown>).layout_name as string | undefined,
                    masterId: (r.layout as Record<string, unknown>).master_id as string | undefined,
                    masterName: (r.layout as Record<string, unknown>).master_name as string | undefined,
                    description: (r.layout as Record<string, unknown>).description as string | undefined,
                } : undefined;
                
                return {
                    resultType,
                    resultId,
                    contextId, // Add contextId for LLM to reference
                    presentationId,
                    slideId,
                    slideIndex: r.slide_index as number | undefined,
                    shapeId,
                    shapeType: r.shape_type as string | undefined,
                    layoutId: r.layout_id as string | undefined,
                    layoutName: r.layout_name as string | undefined,
                    score: r.score as number,
                    similarity: r.similarity as number | undefined,
                    description: r.description as string | undefined,
                    text: r.text as string | undefined,
                    imageBase64: r.image_base64 as string | undefined,
                    metadata: r.metadata as Record<string, unknown> | undefined,
                    slideml: r.slideml as Record<string, unknown> | undefined,
                    // Enriched data
                    shapes,
                    analysis,
                    layout,
                };
            });
            
            // ═══════════════════════════════════════════════════════════════════
            // IMAGE HANDLING: Store images in contextResolver for vision injection
            // Images are NOT included in tool result text (would bloat context).
            // Instead, they're stored and referenced by _imageIds for automatic
            // injection in the next user message by the agent's tool loop.
            // ═══════════════════════════════════════════════════════════════════
            const contextResolver = getContextResolver();
            const imageIds: string[] = [];
            
            for (const result of results) {
                if (result.imageBase64) {
                    // Use contextId as the image ID for easy reference
                    const imageId = result.contextId || result.resultId;
                    
                    // Store image in context resolver
                    // Use async version to automatically resize if exceeds limits
                    const processed = await contextResolver.storeImageAsync(
                        imageId,
                        result.imageBase64,
                        'image/png',
                        {
                            resultType: result.resultType,
                            presentationId: result.presentationId,
                            slideId: result.slideId,
                            score: result.score,
                        }
                    );
                    
                    imageIds.push(imageId);
                    if (processed.wasResized) {
                        console.log(`[SearchPresentationTool] Image ${imageId} was resized for LLM limits`);
                    }
                    
                    // Clear imageBase64 from result to avoid bloating tool result text
                    // The image is now stored in contextResolver and will be injected
                    // in the next user message via the _imageIds convention
                    result.imageBase64 = undefined;
                }
            }
            
            if (imageIds.length > 0) {
                console.log(`[SearchPresentationTool] 🖼️ Stored ${imageIds.length} images for vision injection`);
            }

            const responseDeckScope = useAllScope
                ? 'ALL'
                : presentationIdsForBackend
                    ? presentationIdsForBackend.join(',')
                    : (documentIdForBackend || 'CURRENT');
            
            const response: SearchPresentationResponse = {
                results,
                count: results.length,
                searchType: params.searchType,
                resultTypes: params.resultTypes,
                query: params.query,
                documentId: responseDeckScope,
                searchTimeMs: backendResponse.search_time_ms,
                // Add image IDs for automatic injection by extractImageIds()
                _imageIds: imageIds.length > 0 ? imageIds : undefined,
            };
            
            console.log(`[SearchPresentationTool] ✅ Found ${results.length} results in ${response.searchTimeMs || '?'}ms`);
            
            // Log result summary
            const byType = results.reduce((acc, r) => {
                acc[r.resultType] = (acc[r.resultType] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);
            console.log(`[SearchPresentationTool] Results by type: ${JSON.stringify(byType)}`);
            
            return response;
            
        } catch (error) {
            console.error("[SearchPresentationTool] ❌ Search error:", error);
            throw error;
        }
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type { SearchType, ResultType } from "./searchPresentation.schema";




